import React, { ReactNode, useState, useEffect, useRef } from 'react'

import {
  supportedLanguages,
  CountryConfiguration,
  PurchaseGetDraftResponse,
  calculatePricing,
  GameId,
  formatPriceText,
} from '@common'
import cx from 'classnames'
import { LazyMotion, AnimatePresence, m } from 'framer-motion'
import { useTranslations } from 'next-intl'
import Image, { StaticImageData } from 'next/image'
import { useRouter } from 'next/router'

import { useUpdateDraftMutationState, useGetDraftQuery } from '@/api'
import BsCart from '@/assets/images/brawlstars/bs-cart-icon.png'
import BsEmptyCardBG from '@/assets/images/brawlstars/bs-empty-cart-bg.webp'
import CocCart from '@/assets/images/clashofclans/coc-cart.png'
import CocEmptyCardBG from '@/assets/images/clashofclans/coc-empty-card-bg.png'
import CrCart from '@/assets/images/clashroyale/cr-cart-icon.png'
import CrEmptyCardBG from '@/assets/images/clashroyale/cr-empty-cart-bg.webp'
import { Button } from '@/components/buttons/Button'
import { IconButton } from '@/components/buttons/IconButton'
import { Cross, ArrowRight, SupportIcon } from '@/components/Icons'
import { CheckoutRow } from '@/features/checkout/checkoutSlice'
import { useIsLoggedIn } from '@/features/common/useIsLoggedIn'
import { LoginButton } from '@/features/header/LoginButton'
import { useLocale } from '@/features/locale/useLocale'
import { gtmBeginCheckout } from '@/utils/gtm'
import { snowplowTrackCheckout } from '@/utils/snowplow'
import { useAppSelector } from '@/utils/useAppSelector'

import { StoreTypography } from '../typography/StoreTypography'
import { useCart } from './cart-provider'
import { EligibilityCopy } from './EligibilityCopy'
import { ProductRowList } from './ProductRow'

import styles from './cart.module.scss'

const cartIcons: Record<string, StaticImageData> = {
  clashofclans: CocCart,
  brawlstars: BsCart,
  clashroyale: CrCart,
}

const cartBgs: Record<string, StaticImageData> = {
  clashofclans: CocEmptyCardBG,
  brawlstars: BsEmptyCardBG,
  clashroyale: CrEmptyCardBG,
}

const CartHeader: React.FC<{
  subtitle?: string | JSX.Element | ReactNode
  onClose: () => void
  inEligibleRow: CheckoutRow | undefined
}> = ({ subtitle, onClose, inEligibleRow }) => {
  const gameId = useAppSelector((state) => state.common.currentGame)
  const cartIcon = cartIcons && gameId ? cartIcons[gameId] : null
  return (
    <div className={styles.HeaderContent}>
      <div className={styles.Header}>
        <div>
          {cartIcon ? <Image src={cartIcon} alt="Clash of Clans" width={40} height={38} /> : null}
          <p>{subtitle}</p>
        </div>
        <IconButton className={styles.CloseButton} onClick={onClose} data-cy="cart-button-close">
          <Cross height={32} width={32} />
        </IconButton>
      </div>
      <div className={styles.HeaderEligibility}>
        <AnimatePresence>
          {inEligibleRow && <EligibilityCopy row={inEligibleRow} className={styles.CartEligibility} />}
        </AnimatePresence>
      </div>
    </div>
  )
}

const resolveCartTotal = (
  rows: CheckoutRow[],
  market: CountryConfiguration | undefined,
  draft: PurchaseGetDraftResponse | undefined,
) => {
  if (!market) {
    return 0
  }
  if (draft) {
    return draft.checkout.total
  }
  return calculatePricing(
    rows.map(({ product: { id: sku, prices, game }, quantity }) => ({ sku, prices, quantity, game })),
    market.currencyCountry,
    market.currencyCode,
    { type: market.taxType, percentage: market.tax ?? 0 },
  ).total
}

const EmptyCart = () => {
  const t = useTranslations()
  const gameId = useAppSelector((state) => state.common.currentGame)
  const cartBg = cartBgs && gameId ? cartBgs[gameId] : null
  return (
    <div className={styles.CartEmpty}>
      <p>{t('summary_no_products')}</p>
      <div className={styles.EmptyCardBg}>
        {cartBg ? <Image src={cartBg} alt="empty cart" width={390} style={{ objectFit: 'cover' }} /> : null}
      </div>
    </div>
  )
}

type CartProps = {
  itemCount: number
  formattedItemCount: string
  onClose: () => void
  isLoggedIn: boolean
  rows: CheckoutRow[]
  gameId?: GameId
}

const CartContent = React.forwardRef<HTMLDivElement, CartProps>(
  ({ formattedItemCount, onClose, itemCount, isLoggedIn, gameId, rows }, ref) => {
    const { push } = useRouter()
    const t = useTranslations()
    const locale = useLocale()
    const { isLoading, isError } = useUpdateDraftMutationState()
    const { data: draft, isLoading: draftLoading } = useGetDraftQuery(gameId, { skip: !isLoggedIn || !gameId })
    const isEmpty = rows.length === 0
    const inEligibleRow = draft?.rows.find((row) => !row.eligibility?.isEligible)
    const inMaintenance = useAppSelector((state) => state.config.inMaintenance[gameId as GameId])
    const paymentDisabled = inMaintenance || isEmpty || inEligibleRow !== undefined || isLoading || isError
    const market = useAppSelector((selector) => selector.config.market ?? undefined)
    const taxType = market?.taxType
    const currency = draft?.checkout.currency ?? market?.currencyCode
    const total = resolveCartTotal(rows, market, draft)
    const isRTL = supportedLanguages[locale].rightToLeft
    const isInEligible = draft?.rows.some((r) => !r.eligibility.isEligible)
    const [isNavigating, setIsNavigating] = useState(false)

    const gotoCheckout = () => {
      setIsNavigating(true)
      push(`/game/${gameId}/checkout`)
      gtmBeginCheckout(rows, market?.countryCode)
      snowplowTrackCheckout()
    }

    useEffect(() => {
      document.body.classList.add('overflow-hidden')
      return () => document.body.classList.remove('overflow-hidden')
    }, [])

    return (
      <>
        <m.div
          className={cx(styles.CartDropdown, { [styles.InEligible]: isInEligible })}
          ref={ref}
          data-cy="cart-dropdown"
          initial={{ x: isRTL ? -400 : 400 }}
          animate={{ x: 0 }}
          exit={{ x: isRTL ? -400 : 400 }}
          transition={{ duration: 0.2 }}
        >
          <CartHeader
            subtitle={t.rich('cart_items', { count: itemCount, num: formattedItemCount })}
            onClose={onClose}
            inEligibleRow={inEligibleRow}
          />
          {isEmpty ? (
            <EmptyCart />
          ) : (
            <div className={styles.CartContent}>
              <div className={styles.CartListContainer}>
                <div className={styles.CartList} data-cy="cart-list">
                  <ProductRowList products={rows} className={styles.ProductRow} draft={draft} />
                </div>
              </div>
              <div className={styles.CartFooter}>
                <div className={styles.CheckoutArea}>
                  <div className={styles.Totals}>
                    <p className={styles.CartTotalTitle}>
                      {taxType === 'SALES_TAX' ? t('checkout_cart_sub') : t('summary_total')}
                    </p>
                    <p data-cy="cart-total" className={styles.CartTotalAmount}>
                      {total && !draftLoading && !isLoading ? formatPriceText(total, locale, currency) : '-'}
                    </p>
                  </div>
                  {isLoggedIn ? (
                    <Button
                      disabled={paymentDisabled}
                      additionalClasses={styles.CheckoutButton}
                      loading={isLoading || isNavigating}
                      data-cy="cart-button-checkout"
                      onClick={gotoCheckout}
                    >
                      <div className={styles.CheckoutButtonContent}>
                        <ArrowRight />
                        {t('checkout_checkout')}
                      </div>
                    </Button>
                  ) : (
                    <LoginButton additionalClasses={styles.LoginButton} data-cy="cart-login" />
                  )}
                </div>
                {isLoggedIn && (
                  <div className={styles.HelpShift}>
                    <StoreTypography
                      tag="div"
                      textSize="body"
                      style="plain"
                      overrideFont="supercellText"
                      className={styles.helpshiftText}
                    >
                      {t('help_helpshift')}
                    </StoreTypography>
                    <div onClick={() => window.Helpshift?.('open')}>
                      <SupportIcon height={50} width={50} />
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </m.div>
        <m.div
          key="cart-backdrop"
          className={styles.backdrop}
          onClick={onClose}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4 }}
        />
      </>
    )
  },
)

CartContent.displayName = 'CartContent'

export const Cart = () => {
  const isLoggedIn = useIsLoggedIn()
  const locale = useLocale()
  const gameId = useAppSelector((state) => state.common.currentGame)
  const rows = useAppSelector((state) => state.checkout.rows).filter((r) => r.product.game === gameId)
  const { isOpen, setIsOpen } = useCart()
  const contentRef = useRef<HTMLDivElement>(null)

  const itemCount = rows.reduce<number>((acc, curr) => acc + curr.quantity, 0)
  const formattedItemCount = new Intl.NumberFormat(supportedLanguages[locale].locale).format(Math.min(itemCount, 99))

  const features = () => import('@/animations/features').then((a) => a.default)
  return (
    <LazyMotion features={features}>
      <AnimatePresence>
        {isOpen ? (
          <CartContent
            itemCount={itemCount}
            formattedItemCount={formattedItemCount}
            ref={contentRef}
            onClose={() => setIsOpen(false)}
            isLoggedIn={isLoggedIn}
            gameId={gameId}
            rows={rows}
          />
        ) : null}
      </AnimatePresence>
    </LazyMotion>
  )
}
